import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  UploadSimple,
  FileCsv,
  Sparkle,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { AppHeader } from "@/components/AppHeader";
import { api, formatApiErrorDetail } from "@/lib/api";

const COLUMNS = [
  "model",
  "brand",
  "description",
  "sink_type",
  "bowls",
  "size",
  "material",
  "supplier",
  "cost",
  "sale_price",
  "location",
  "quantity",
  "min_level",
  "notes",
];

export default function SinkImport() {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [fileName, setFileName] = useState("");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("add");

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/sinks/import/preview", fd);
      setPreview(data);
      setFileName(file.name);
      toast.success(
        `${data.count} row${data.count === 1 ? "" : "s"} read — review below`,
      );
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not read that file",
      );
    }
    setBusy(false);
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/sinks/import", {
        rows: preview.rows,
        mode,
      });
      toast.success(
        mode === "update"
          ? `${data.updated} topped up · ${data.created} new sinks added`
          : `${data.created} sinks added to inventory`,
      );
      navigate("/inventory/sinks");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Import failed",
      );
    }
    setBusy(false);
  };

  const template = () => {
    const blob = new Blob(
      [
        `${COLUMNS.join(",")}\n3018,KDC,Single bowl undermount,Undermount,Single,30"x18",Stainless,Stone Source,55,199,Shelf B-2,4,1,\n`,
      ],
      { type: "text/csv" },
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "sink-import-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="grain min-h-screen">
      <AppHeader title="Settings / Import Sinks" />
      <main className="relative z-10 pb-28 pt-6">
        <Link
          to="/settings"
          data-testid="back-to-settings"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 hover:text-white"
        >
          <ArrowLeft size={14} weight="bold" /> Settings
        </Link>
        <h1 className="mt-3 text-2xl font-bold tracking-tight sm:text-3xl">
          Import your sinks
        </h1>
        <p className="mt-1 text-sm text-zinc-500">
          Upload a CSV of your sink stock. Every row is shown for review before
          anything is saved.
        </p>

        <div className="mt-6 rounded-lg border border-zinc-800 bg-zinc-900 p-6">
          <div className="rounded-lg border border-dashed border-zinc-700 p-10 text-center">
            <UploadSimple
              size={30}
              weight="bold"
              className="mx-auto text-zinc-500"
            />
            <div className="mt-3 text-base font-semibold">
              Drop your sink list here
            </div>
            <div className="mt-1 text-xs text-zinc-500">
              CSV with a header row — up to 15MB
            </div>
            <div className="mt-5 flex flex-wrap justify-center gap-2">
              <Button
                data-testid="choose-sink-file"
                disabled={busy}
                onClick={() => inputRef.current?.click()}
                className="h-11 bg-indigo-600 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-500"
              >
                <FileCsv size={16} weight="bold" className="mr-2" />
                {busy ? "Reading…" : "Choose file"}
              </Button>
              <Button
                data-testid="download-sink-template"
                variant="outline"
                onClick={template}
                className="h-11 border-zinc-700 bg-transparent text-xs uppercase tracking-widest hover:bg-zinc-800"
              >
                Download template
              </Button>
            </div>
            <input
              ref={inputRef}
              data-testid="sink-import-input"
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={pick}
            />
          </div>
          <p className="mt-4 flex items-start gap-2 text-xs text-zinc-500">
            <Sparkle
              size={14}
              weight="bold"
              className="mt-0.5 shrink-0 text-indigo-300"
            />
            Column headers are matched by name (item_code and sku both work for
            the model). Only the item code is required — every other column can
            be left blank.
          </p>
        </div>

        <div
          className="mt-4 flex flex-wrap gap-2"
          data-testid="sink-import-mode-toggle"
        >
          {[
            ["add", "Add as new sinks"],
            ["update", "Top up existing item codes"],
          ].map(([v, label]) => (
            <button
              key={v}
              data-testid={`sink-import-mode-${v}`}
              onClick={() => setMode(v)}
              className={`rounded-full border px-4 py-2 text-[11px] uppercase tracking-widest transition-colors duration-150 ${
                mode === v
                  ? "border-indigo-400/50 bg-indigo-500/10 text-indigo-300"
                  : "border-zinc-800 bg-zinc-900 text-zinc-500 hover:text-zinc-300"
              }`}
            >
              {label}
            </button>
          ))}
          <span className="self-center text-xs text-zinc-500">
            {mode === "update"
              ? "Rows matching an existing item code add to its on-hand count; new codes are created."
              : "Every row becomes a new sink record."}
          </span>
        </div>

        {preview && (
          <div className="mt-6" data-testid="sink-import-preview">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-xs uppercase tracking-widest text-zinc-500">
                  {fileName}
                </div>
                <div className="text-sm text-zinc-300">
                  <span className="font-mono-data text-emerald-400">
                    {preview.count}
                  </span>{" "}
                  ready to import
                  {preview.errors.length > 0 && (
                    <>
                      {" · "}
                      <span className="font-mono-data text-red-400">
                        {preview.errors.length}
                      </span>{" "}
                      skipped
                    </>
                  )}
                </div>
              </div>
              <Button
                data-testid="confirm-sink-import-button"
                disabled={busy || preview.count === 0}
                onClick={confirm}
                className="h-11 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
              >
                {busy
                  ? "Importing…"
                  : mode === "update"
                    ? `Top Up From ${preview.count} Rows`
                    : `Import ${preview.count} Sinks`}
              </Button>
            </div>

            {preview.errors.length > 0 && (
              <div
                className="mt-3 rounded-lg border border-red-500/30 bg-red-500/5 p-4"
                data-testid="sink-import-errors"
              >
                {preview.errors.slice(0, 8).map((e) => (
                  <div key={e} className="text-xs text-red-300">
                    {e}
                  </div>
                ))}
              </div>
            )}

            <div className="mt-3 overflow-x-auto rounded-lg border border-zinc-800 bg-zinc-900">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-zinc-800 text-[10px] uppercase tracking-widest text-zinc-500">
                    <th className="p-3">Row</th>
                    <th className="p-3">Item code</th>
                    <th className="p-3">Brand</th>
                    <th className="p-3">Spec</th>
                    <th className="p-3">Shelf</th>
                    <th className="p-3 text-right">Qty</th>
                    <th className="p-3 text-right">Cost</th>
                    <th className="p-3 text-right">Sale</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r) => (
                    <tr
                      key={r._row}
                      data-testid={`sink-import-row-${r._row}`}
                      className="border-b border-zinc-800 last:border-0"
                    >
                      <td className="font-mono-data p-3 text-xs text-zinc-500">
                        {r._row}
                      </td>
                      <td className="font-mono-data p-3 font-semibold">
                        {r.model}
                      </td>
                      <td className="p-3 text-zinc-400">{r.brand || "—"}</td>
                      <td className="p-3 text-zinc-400">
                        {[r.sink_type, r.bowls, r.size, r.material]
                          .filter(Boolean)
                          .join(" · ")}
                      </td>
                      <td className="p-3 text-zinc-400">{r.location || "—"}</td>
                      <td className="font-mono-data p-3 text-right text-zinc-300">
                        {r.quantity}
                      </td>
                      <td className="font-mono-data p-3 text-right text-zinc-400">
                        {r.cost != null ? `$${r.cost}` : "—"}
                      </td>
                      <td className="font-mono-data p-3 text-right text-indigo-300">
                        {r.sale_price != null ? `$${r.sale_price}` : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
